import * as Yup from "yup";
import { forceInt } from "Utils";
import { transform as lockTransformer } from "./Lock";
import { transform as workflowTransformer } from "./Workflow";
import { transform as authorTransformer } from "./Author";
import { transform as tagTransformer } from "./Tag";
import { transform as collectionTransformer } from "./Collection";
import { transform as attachmentTransformer } from "./Attachment";
import { transform as metaPropertyTransformer } from "./MetaProperty";
import { transform as containerTransformer } from "./Container";
import { transform as imageKeywordTransformer } from "./ImageKeyword";
import { transform as imageMetadataTransformer } from "./ImageMetadata";
import { transform as urlTransformer } from "./Url";
import { fromSqlDateTime } from "../helpers/Date";

export const ContentValidationSchema = Yup.object().shape({});

export const ContentObjectSchema = {
    id: null,
    uuid: null,
    srn: null,
    slug: "",
    content: null,
    content_raw: null,
    abstract: null,
    abstract_raw: null,
    title: "",
    title_url: "",
    type: null,
    created_at: null,
    modified_at: null,
    published_at: null,
    weight: 0,
    status: null,
    version: null,
    seo_title: "",
    seo_description: "",
    seo_url: "",
    layout_template: "Default",
    ssts_id: null,
    ssts_path: null,
    click_through: null,
    tags: [],
    collections: [],
    authors: [],
    metaProperties: [],
    keywords: [],
    imageKeywords: [],
    related: [],
    siblings: [],
    urls: [],
    dominantAttachment: null,
    attachment: null,
    container: null,
};

/**
 * Transform API object to a Content object
 * @param {object} requestData JSON object from the API
 * @returns Object
 */
export function transform(requestData) {
    if (requestData.data) {
        return requestData.data.map((item) => transform(item));
    }

    let content = Object.assign({}, ContentObjectSchema, {
        id: forceInt(requestData.id),
        uuid: requestData.uuid,
        srn: requestData.srn,
        slug: requestData.slug,
        title_url: requestData.title_url,
        content: requestData.content,
        content_raw: requestData.content_raw,
        abstract: requestData.abstract,
        abstract_raw: requestData.abstract_raw,
        title: requestData.title,
        type: requestData.type,
        created_at: requestData.created_at,
        modified_at: requestData.modified_at,
        published_at: requestData.published_at,
        weight: requestData.weight,
        status: requestData.status,
        layout_template: requestData.layout_template
            ? requestData.layout_template
            : "Default",
        version: forceInt(requestData.version),
        seo_title: requestData.seo_title,
        seo_description: requestData.seo_description,
        seo_url: requestData.seo_url,
        ssts_id: requestData.ssts_id,
        ssts_path: requestData.ssts_path,
        click_through: requestData.click_through,
        last_changed: fromSqlDateTime(requestData.modified_at).toMillis(),
        edit_url: `/app/${requestData.type}/${requestData.uuid}`,
        object_type: "content",
    });

    if (requestData.lock) {
        let lock = lockTransformer(requestData.lock);
        content = Object.assign({}, content, { lock: lock });
    }

    if (requestData.workflow) {
        let wf = workflowTransformer(requestData.workflow);
        content = Object.assign({}, content, { workflow: wf });
    }

    if (requestData.authors) {
        content = Object.assign({}, content, {
            authors: authorTransformer(requestData.authors),
        });
    }

    if (requestData.tags) {
        content = Object.assign({}, content, {
            tags: tagTransformer(requestData.tags),
        });
    }

    if (requestData.collections) {
        content = Object.assign({}, content, {
            collections: collectionTransformer(requestData.collections),
        });
    }

    if (requestData.metaProperties) {
        content = Object.assign({}, content, {
            metaProperties: metaPropertyTransformer(requestData.metaProperties),
        });
    }

    if (requestData.dominantAttachment) {
        content = Object.assign({}, content, {
            dominantAttachment: transform(requestData.dominantAttachment),
        });
    }

    if (requestData.attachment) {
        content = Object.assign({}, content, {
            attachment: attachmentTransformer(requestData.attachment),
        });
    }

    if (requestData.container) {
        content = Object.assign({}, content, {
            container: containerTransformer(requestData.container),
        });
    }

    if (requestData.keywords) {
        content = Object.assign({}, content, {
            keywords: requestData.keywords,
        });
    }

    if (requestData.imageKeywords) {
        content = Object.assign({}, content, {
            imageKeywords: imageKeywordTransformer(requestData.imageKeywords),
        });
    }

    if (requestData.imageMetadata) {
        content = Object.assign({}, content, {
            imageMetadata: imageMetadataTransformer(requestData.imageMetadata),
        });
    }

    if (requestData.urls) {
        content = Object.assign({}, content, {
            urls: urlTransformer(requestData.urls),
        });
    }

    if (requestData.related) {
        content = Object.assign({}, content, {
            related: transform(requestData.related),
        });
    }

    if (requestData.siblings) {
        content = Object.assign({}, content, {
            siblings: transform(requestData.siblings),
        });
    }

    return content;
}

/**
 * Serialize the content object to a string
 * @param {object} item
 * @returns string
 */
export function serialize(item) {
    return JSON.stringify(item);
}

/**
 * Deserialize json string
 * @param {string} item
 * @returns object
 */
export function deserialize(item) {
    return JSON.parse(item);
}

/**
 *
 * @param {object} item The object being saved
 * @param {object} resetVars Values to overwrite after preparation
 * @returns Object
 */
export function prepare(item, resetVars = {}) {
    return Object.assign(
        {},
        item,
        {
            attachment: undefined,
            last_changed: undefined,
            modified_at: undefined,
            edit_url: undefined,
            object_type: undefined,
            custom_url: undefined,
        },
        resetVars
    );
}
